/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import {FieldWrapperPassThroughProps} from './AppFieldWrapper';
import React from "react";
import ReCAPTCHA from 'react-google-recaptcha';
import {UseFormRegisterReturn} from 'react-hook-form';

type AppRecaptchaProps = FieldWrapperPassThroughProps & {
	siteKey: string;
	onChange: (token: string | null) => void;
	onExpired?: () => void;
	onErrored?: () => void;
	theme?: 'light' | 'dark';
	size?: 'compact' | 'normal' | 'invisible';
	className?: string;
	mt?: string;
	mb?: string;
	registration: Partial<UseFormRegisterReturn>;
};

export const AppRecaptcha = (props: AppRecaptchaProps) => {
	const {
		siteKey,
		onChange,
		onExpired,
		onErrored,
		theme = 'light',
		size = 'normal',
		className = '',
		error,
		mt = '',
		mb = '',
	} = props;

	const recaptchaRef = React.useRef<ReCAPTCHA>(null);

	const handleChange = (token: string | null) => {
		onChange(token);
	};

	const handleExpired = () => {
		onChange(null);
		if (onExpired) {
			onExpired();
		}
	};

	const handleError = () => {
		onChange(null);
		if (onErrored) {
			onErrored();
		}
	};

	return (
		<div className={`${mt} ${mb} ${className}`}>
			<ReCAPTCHA
				ref={recaptchaRef}
				sitekey={siteKey}
				onChange={handleChange}
				onExpired={handleExpired}
				onErrored={handleError}
				theme={theme}
				size={size}
			/>

			{error?.message && (
				<div role="alert" aria-label={error.message} className="mt-2 text-sm font-semibold text-red-500">
					{error.message}
				</div>
			)}
		</div>
	);
};
